import { ArrowRight, ShieldAlert, Compass, Gauge, FileCheck2, Handshake, ListChecks, AlertTriangle, ScrollText } from "lucide-react";
import { cn } from "@/lib/utils";

const URGENCY_STYLES = {
  critical: { chip: "bg-destructive text-destructive-foreground border-destructive", dot: "bg-destructive-foreground" },
  high: { chip: "bg-destructive-soft text-destructive border-destructive/40", dot: "bg-destructive" },
  medium: { chip: "bg-warning-soft text-warning border-warning/40", dot: "bg-warning" },
  low: { chip: "bg-info-soft text-info border-info/40", dot: "bg-info" },
  na: { chip: "bg-secondary text-muted-foreground border-border", dot: "bg-muted-foreground" }
};

const EXPOSURE_STYLES = {
  high: { chip: "bg-destructive-soft text-destructive border-destructive/40" },
  medium: { chip: "bg-warning-soft text-warning border-warning/40" },
  low: { chip: "bg-info-soft text-info border-info/40" },
  na: { chip: "bg-secondary text-muted-foreground border-border" }
};

// Mapping for the four controlled-output Source Verification states.
const SOURCE_STYLES = {
  verified: { chip: "bg-success-soft text-success border-success/40", icon: FileCheck2 },
  requires_source_verification: { chip: "bg-warning-soft text-warning border-warning/40", icon: AlertTriangle },
  internal_only: { chip: "bg-info-soft text-info border-info/40", icon: ScrollText },
  do_not_publish: { chip: "bg-destructive text-destructive-foreground border-destructive", icon: ShieldAlert }
};

function Field({ icon: Icon, eyebrow, title, description, chipClass, chipLabel, testid }) {
  const titleMatchesChip = chipLabel && chipLabel.toLowerCase() === String(title).toLowerCase();
  return (
    <div
      data-testid={testid}
      className="classifier-field flex flex-col gap-2 rounded-sm border border-border bg-card/70 p-5"
    >
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-sm bg-secondary text-foreground">
          <Icon className="h-4 w-4" />
        </span>
        <p className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {eyebrow}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {titleMatchesChip ? (
          <span
            className={cn(
              "inline-flex shrink-0 rounded-sm border px-2.5 py-1 font-mono-label text-[11px] uppercase tracking-[0.18em]",
              chipClass
            )}
          >
            {chipLabel}
          </span>
        ) : (
          <>
            <p className="font-display text-[15px] font-medium leading-snug text-foreground">{title}</p>
            {chipLabel && (
              <span
                className={cn(
                  "inline-flex shrink-0 rounded-sm border px-2 py-0.5 font-mono-label text-[10px] uppercase tracking-[0.18em]",
                  chipClass
                )}
              >
                {chipLabel}
              </span>
            )}
          </>
        )}
      </div>
      {description && (
        <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
      )}
    </div>
  );
}

export function ClassifierOutputCard({ classifier }) {
  if (!classifier) return null;
  const urgencyStyle = URGENCY_STYLES[classifier.urgency.level] || URGENCY_STYLES.medium;
  const exposureStyle = EXPOSURE_STYLES[classifier.exposure.level] || EXPOSURE_STYLES.medium;
  const sourceStyle = SOURCE_STYLES[classifier.sourceVerification.status] || SOURCE_STYLES.requires_source_verification;

  return (
    <section
      data-testid="classifier-output-card"
      className="classifier-card paper-card overflow-hidden"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card/80 px-6 py-4">
        <div>
          <p className="eyebrow">§ 04 · Classifier output</p>
          <h3 className="font-display text-lg font-medium text-foreground">
            Provider pathway · 0138 exposure · urgency
          </h3>
        </div>
        <span className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          9-field classification
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 px-6 py-6 md:grid-cols-2 xl:grid-cols-3">
        <Field
          testid="classifier-pathway"
          icon={Compass}
          eyebrow="01 · Provider pathway"
          title={classifier.pathway.title}
          description={classifier.pathway.description}
        />
        <Field
          testid="classifier-exposure"
          icon={Gauge}
          eyebrow="02 · 0138 exposure likelihood"
          title={classifier.exposure.label}
          description={classifier.exposure.description}
          chipClass={exposureStyle.chip}
          chipLabel={classifier.exposure.label}
        />
        <Field
          testid="classifier-urgency"
          icon={ShieldAlert}
          eyebrow="03 · Urgency rating"
          title={classifier.urgency.label}
          description={classifier.urgency.description}
          chipClass={urgencyStyle.chip}
          chipLabel={classifier.urgency.label}
        />
        <Field
          testid="classifier-source-verification"
          icon={sourceStyle.icon}
          eyebrow="04 · Source verification status"
          title={classifier.sourceVerification.label}
          description={classifier.sourceVerification.description}
          chipClass={sourceStyle.chip}
          chipLabel={classifier.sourceVerification.label}
        />
        <Field
          testid="classifier-recommended-offer"
          icon={Handshake}
          eyebrow="05 · Recommended service offer"
          title={classifier.recommendedOffer.title}
          description={classifier.recommendedOffer.description}
        />
        <Field
          testid="classifier-next-action"
          icon={ArrowRight}
          eyebrow="09 · Next action"
          title={classifier.nextAction}
          description={null}
        />
      </div>

      <div className="grid grid-cols-1 gap-0 border-t border-border md:grid-cols-2">
        <div
          data-testid="classifier-evidence-needed"
          className="classifier-panel border-b border-border p-6 md:border-b-0 md:border-r"
        >
          <div className="mb-3 flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-foreground" />
            <p className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              06 · Evidence needed
            </p>
            <span className="ml-auto font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {classifier.evidenceNeeded.length} item{classifier.evidenceNeeded.length === 1 ? "" : "s"}
            </span>
          </div>
          {classifier.evidenceNeeded.length === 0 ? (
            <p className="text-sm text-muted-foreground">No outstanding evidence recorded.</p>
          ) : (
            <ul className="space-y-2">
              {classifier.evidenceNeeded.slice(0, 8).map((item) => (
                <li
                  key={item.id}
                  className="flex items-start gap-2 text-[13px] leading-relaxed text-foreground"
                >
                  <span
                    className={cn(
                      "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full",
                      item.rag === "red" ? "bg-destructive" : "bg-warning"
                    )}
                  />
                  <span>
                    <span className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      [{item.domain}]
                    </span>{" "}
                    {item.title} — <span className="text-muted-foreground">{item.label}</span>
                  </span>
                </li>
              ))}
              {classifier.evidenceNeeded.length > 8 && (
                <li className="text-xs italic text-muted-foreground">
                  + {classifier.evidenceNeeded.length - 8} more in the full evidence table below.
                </li>
              )}
            </ul>
          )}
        </div>

        <div data-testid="classifier-risks-requiring-review" className="classifier-panel p-6">
          <div className="mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-foreground" />
            <p className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              07 · Risks requiring review
            </p>
            <span className="ml-auto font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {classifier.risksRequiringReview.length} item{classifier.risksRequiringReview.length === 1 ? "" : "s"}
            </span>
          </div>
          {classifier.risksRequiringReview.length === 0 ? (
            <p className="text-sm text-muted-foreground">No risks requiring review recorded.</p>
          ) : (
            <ul className="space-y-2">
              {classifier.risksRequiringReview.slice(0, 8).map((risk) => (
                <li
                  key={risk.id}
                  className="flex items-start gap-2 text-[13px] leading-relaxed text-foreground"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" />
                  <span>
                    <span className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      [{risk.source}]
                    </span>{" "}
                    {risk.label}
                  </span>
                </li>
              ))}
              {classifier.risksRequiringReview.length > 8 && (
                <li className="text-xs italic text-muted-foreground">
                  + {classifier.risksRequiringReview.length - 8} more.
                </li>
              )}
            </ul>
          )}
        </div>
      </div>

      <div
        data-testid="classifier-boundary-statement"
        className="classifier-boundary border-t border-border bg-muted/40 px-6 py-4 text-xs leading-relaxed text-muted-foreground"
      >
        <span className="font-mono-label text-[10px] uppercase tracking-[0.18em]">
          08 · Boundary statement —{" "}
        </span>
        {classifier.boundaryStatement}
      </div>
    </section>
  );
}
